import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  Type,
  ViewEncapsulation,
} from '@angular/core';

export type IllustrationName =
  'accepted-agreement' |
  'account-owner' |
  'advanced-security' |
  'ai-describe' |
  'ai-upload' |
  'answer-approver' |
  'attributes' |
  'briefcase' |
  'briefcase-denied' |
  'briefcase-info' |
  'briefcase-notification' |
  'briefcase-search' |
  'briefcase-warn' |
  'checklist' |
  'cloud-archive' |
  'copy-folder' |
  'corrupted-file' |
  'dashboard' |
  'desktop' |
  'detect-pii' |
  'document-versions' |
  'downloads' |
  'drop-excel' |
  'duplicated-indexes' |
  'excel-error' |
  'export-index' |
  'feedback' |
  'file-type-not-supported' |
  'gift' |
  'incorrect-time' |
  'invite-admins' |
  'invite-teammate' |
  'invite-teammates' |
  'launch-project' |
  'long-process' |
  'manage-labels' |
  'new-invoice' |
  'no-access' |
  'no-api-keys' |
  'no-chart-data' |
  'no-data' |
  'no-documents' |
  'no-documents-notes' |
  'no-downloads' |
  'no-excel-data' |
  'no-favorite-documents' |
  'no-folder-structure' |
  'no-labels' |
  'no-notifications' |
  'no-outlines' |
  'no-projects' |
  'no-proposals' |
  'no-qna-faq-threads' |
  'no-qna-threads' |
  'no-recent-documents' |
  'no-recovery-codes' |
  'no-renamed-items' |
  'no-roles' |
  'no-shared-files' |
  'no-subscriptions' |
  'no-text-content' |
  'no-unpublished-documents' |
  'no-uploaded-documents' |
  'no-users' |
  'no-views' |
  'not-accepted-agreement' |
  'online-archive' |
  'overdue-invoices' |
  'participants-across-projects' |
  'project-closure' |
  'project-template' |
  'public-api' |
  'qna-responses' |
  'recovery-codes' |
  'recycle-bin' |
  'redaction' |
  'request-upgrade' |
  'search-file' |
  'search-files' |
  'security' |
  'send-for-signature' |
  'shared-file' |
  'shared-links' |
  'sign-document' |
  'signed-document' |
  'sso-integration' |
  'subscription-order' |
  'unlock-project' |
  'upload' |
  'upload-checklist' |
  'upload-excel' |
  'upload-file' |
  'usb-archive';

const ILLUSTRATION_MAP: Record<IllustrationName, () => Promise<Type<unknown>>> = {
  'accepted-agreement': () => import('./icons/accepted-agreement').then((m) => m.AcceptedAgreementComponent),
  'account-owner': () => import('./icons/account-owner').then((m) => m.AccountOwnerComponent),
  'advanced-security': () => import('./icons/advanced-security').then((m) => m.AdvancedSecurityComponent),
  'ai-describe': () => import('./icons/ai-describe').then((m) => m.AiDescribeComponent),
  'ai-upload': () => import('./icons/ai-upload').then((m) => m.AiUploadComponent),
  'answer-approver': () => import('./icons/answer-approver').then((m) => m.AnswerApproverComponent),
  attributes: () => import('./icons/attributes').then((m) => m.AttributesComponent),
  briefcase: () => import('./icons/briefcase').then((m) => m.BriefcaseComponent),
  'briefcase-denied': () => import('./icons/briefcase-denied').then((m) => m.BriefcaseDeniedComponent),
  'briefcase-info': () => import('./icons/briefcase-info').then((m) => m.BriefcaseInfoComponent),
  'briefcase-notification': () => import('./icons/briefcase-notification').then((m) => m.BriefcaseNotificationComponent),
  'briefcase-search': () => import('./icons/briefcase-search').then((m) => m.BriefcaseSearchComponent),
  'briefcase-warn': () => import('./icons/briefcase-warn').then((m) => m.BriefcaseWarnComponent),
  checklist: () => import('./icons/checklist').then((m) => m.ChecklistComponent),
  'cloud-archive': () => import('./icons/cloud-archive').then((m) => m.CloudArchiveComponent),
  'copy-folder': () => import('./icons/copy-folder').then((m) => m.CopyFolderComponent),
  'corrupted-file': () => import('./icons/corrupted-file').then((m) => m.CorruptedFileComponent),
  dashboard: () => import('./icons/dashboard').then((m) => m.DashboardComponent),
  desktop: () => import('./icons/desktop').then((m) => m.DesktopComponent),
  'detect-pii': () => import('./icons/detect-pii').then((m) => m.DetectPiiComponent),
  'document-versions': () => import('./icons/document-versions').then((m) => m.DocumentVersionsComponent),
  downloads: () => import('./icons/downloads').then((m) => m.DownloadsComponent),
  'drop-excel': () => import('./icons/drop-excel').then((m) => m.DropExcelComponent),
  'duplicated-indexes': () => import('./icons/duplicated-indexes').then((m) => m.DuplicatedIndexesComponent),
  'excel-error': () => import('./icons/excel-error').then((m) => m.ExcelErrorComponent),
  'export-index': () => import('./icons/export-index').then((m) => m.ExportIndexComponent),
  feedback: () => import('./icons/feedback').then((m) => m.FeedbackComponent),
  'file-type-not-supported': () => import('./icons/file-type-not-supported').then((m) => m.FileTypeNotSupportedComponent),
  gift: () => import('./icons/gift').then((m) => m.GiftComponent),
  'incorrect-time': () => import('./icons/incorrect-time').then((m) => m.IncorrectTimeComponent),
  'invite-admins': () => import('./icons/invite-admins').then((m) => m.InviteAdminsComponent),
  'invite-teammate': () => import('./icons/invite-teammate').then((m) => m.InviteTeammateComponent),
  'invite-teammates': () => import('./icons/invite-teammates').then((m) => m.InviteTeammatesComponent),
  'launch-project': () => import('./icons/launch-project').then((m) => m.LaunchProjectComponent),
  'long-process': () => import('./icons/long-process').then((m) => m.LongProcessComponent),
  'manage-labels': () => import('./icons/manage-labels').then((m) => m.ManageLabelsComponent),
  'new-invoice': () => import('./icons/new-invoice').then((m) => m.NewInvoiceComponent),
  'no-access': () => import('./icons/no-access').then((m) => m.NoAccessComponent),
  'no-api-keys': () => import('./icons/no-api-keys').then((m) => m.NoApiKeysComponent),
  'no-chart-data': () => import('./icons/no-chart-data').then((m) => m.NoChartDataComponent),
  'no-data': () => import('./icons/no-data').then((m) => m.NoDataComponent),
  'no-documents': () => import('./icons/no-documents').then((m) => m.NoDocumentsComponent),
  'no-documents-notes': () => import('./icons/no-documents-notes').then((m) => m.NoDocumentsNotesComponent),
  'no-downloads': () => import('./icons/no-downloads').then((m) => m.NoDownloadsComponent),
  'no-excel-data': () => import('./icons/no-excel-data').then((m) => m.NoExcelDataComponent),
  'no-favorite-documents': () => import('./icons/no-favorite-documents').then((m) => m.NoFavoriteDocumentsComponent),
  'no-folder-structure': () => import('./icons/no-folder-structure').then((m) => m.NoFolderStructureComponent),
  'no-labels': () => import('./icons/no-labels').then((m) => m.NoLabelsComponent),
  'no-notifications': () => import('./icons/no-notifications').then((m) => m.NoNotificationsComponent),
  'no-outlines': () => import('./icons/no-outlines').then((m) => m.NoOutlinesComponent),
  'no-projects': () => import('./icons/no-projects').then((m) => m.NoProjectsComponent),
  'no-proposals': () => import('./icons/no-proposals').then((m) => m.NoProposalsComponent),
  'no-qna-faq-threads': () => import('./icons/no-qna-faq-threads').then((m) => m.NoQnaFaqThreadsComponent),
  'no-qna-threads': () => import('./icons/no-qna-threads').then((m) => m.NoQnaThreadsComponent),
  'no-recent-documents': () => import('./icons/no-recent-documents').then((m) => m.NoRecentDocumentsComponent),
  'no-recovery-codes': () => import('./icons/no-recovery-codes').then((m) => m.NoRecoveryCodesComponent),
  'no-renamed-items': () => import('./icons/no-renamed-items').then((m) => m.NoRenamedItemsComponent),
  'no-roles': () => import('./icons/no-roles').then((m) => m.NoRolesComponent),
  'no-shared-files': () => import('./icons/no-shared-files').then((m) => m.NoSharedFilesComponent),
  'no-subscriptions': () => import('./icons/no-subscriptions').then((m) => m.NoSubscriptionsComponent),
  'no-text-content': () => import('./icons/no-text-content').then((m) => m.NoTextContentIllustrationComponent),
  'no-unpublished-documents': () => import('./icons/no-unpublished-documents').then((m) => m.NoUnpublishedDocumentsComponent),
  'no-uploaded-documents': () => import('./icons/no-uploaded-documents').then((m) => m.NoUploadedDocumentsComponent),
  'no-users': () => import('./icons/no-users').then((m) => m.NoUsersComponent),
  'no-views': () => import('./icons/no-views').then((m) => m.NoViewsComponent),
  'not-accepted-agreement': () => import('./icons/not-accepted-agreement').then((m) => m.NotAcceptedAgreementComponent),
  'online-archive': () => import('./icons/online-archive').then((m) => m.OnlineArchiveComponent),
  'overdue-invoices': () => import('./icons/overdue-invoices').then((m) => m.OverdueInvoicesComponent),
  'participants-across-projects': () => import('./icons/participants-across-projects').then((m) => m.ParticipantsAcrossProjectsComponent),
  'project-closure': () => import('./icons/project-closure').then((m) => m.ProjectClosureComponent),
  'project-template': () => import('./icons/project-template').then((m) => m.ProjectTemplateComponent),
  'public-api': () => import('./icons/public-api').then((m) => m.PublicApiComponent),
  'qna-responses': () => import('./icons/qna-responses').then((m) => m.QnaResponsesComponent),
  'recovery-codes': () => import('./icons/recovery-codes').then((m) => m.RecoveryCodesComponent),
  'recycle-bin': () => import('./icons/recycle-bin').then((m) => m.RecycleBinComponent),
  redaction: () => import('./icons/redaction').then((m) => m.RedactionComponent),
  'request-upgrade': () => import('./icons/request-upgrade').then((m) => m.RequestUpgradeComponent),
  'search-file': () => import('./icons/search-file').then((m) => m.SearchFileComponent),
  'search-files': () => import('./icons/search-files').then((m) => m.SearchFilesComponent),
  security: () => import('./icons/security').then((m) => m.SecurityComponent),
  'send-for-signature': () => import('./icons/send-for-signature').then((m) => m.SendForSignatureComponent),
  'shared-file': () => import('./icons/shared-file').then((m) => m.SharedFileComponent),
  'shared-links': () => import('./icons/shared-links').then((m) => m.SharedLinksComponent),
  'sign-document': () => import('./icons/sign-document').then((m) => m.SignDocumentComponent),
  'signed-document': () => import('./icons/signed-document').then((m) => m.SignedDocumentComponent),
  'sso-integration': () => import('./icons/sso-integration').then((m) => m.SsoIntegrationComponent),
  'subscription-order': () => import('./icons/subscription-order').then((m) => m.SubscriptionOrderComponent),
  'unlock-project': () => import('./icons/unlock-project').then((m) => m.UnlockProjectComponent),
  upload: () => import('./icons/upload').then((m) => m.UploadComponent),
  'upload-checklist': () => import('./icons/upload-checklist').then((m) => m.UploadChecklistComponent),
  'upload-excel': () => import('./icons/upload-excel').then((m) => m.UploadExcelComponent),
  'upload-file': () => import('./icons/upload-file').then((m) => m.UploadFileComponent),
  'usb-archive': () => import('./icons/usb-archive').then((m) => m.UsbArchiveComponent),
};

@Component({
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'ideals-illustration',
    '[class.ideals-illustration--large]': 'size() === "large"',
    '[class.ideals-illustration--small]': 'size() === "small"',
    fxLayout: 'column',
  },
  imports: [NgComponentOutlet],
  selector: 'ideals-illustration',
  styleUrl: './illustration.component.scss',
  template: '<ng-container *ngComponentOutlet="component()"/>',
})
export class IllustrationComponent implements OnInit {
  readonly name = input.required<IllustrationName>();
  readonly size = input<'large' | 'small'>('large');

  readonly #injector = inject(Injector);

  protected readonly component = signal<Type<unknown> | null>(null);

  ngOnInit(): void {
    this.#effects();
  }

  #effects(): void {
    effect(() => {
      ILLUSTRATION_MAP[this.name()]().then((component) => {
        this.component.set(component);
      });
    }, { injector: this.#injector });
  }
}
